import { QAPair, Conflict } from '../types';

/**
 * Detects conflicts in the dependency graph
 * - Multiple dependencies: When multiple answers depend on the same question
 * - Circular dependencies: When questions create dependency cycles
 */
export function detectConflicts(qaPairs: QAPair[]): Conflict[] {
  const conflicts: Conflict[] = [];

  // Detect multiple dependencies
  conflicts.push(...detectMultipleDependencies(qaPairs));

  // Detect circular dependencies
  conflicts.push(...detectCircularDependencies(qaPairs));

  return conflicts;
}

/**
 * Detect when multiple answers have dependencies pointing to the same question
 */
function detectMultipleDependencies(qaPairs: QAPair[]): Conflict[] {
  const conflicts: Conflict[] = [];

  // Build a map of targetId -> [sourceIds]
  const dependencyMap = new Map<string, string[]>();

  for (const pair of qaPairs) {
    const sourceId = pair.question.id;

    for (const dep of pair.answer.dependencies) {
      const targetId = normalizeNodeId(dep.targetId);

      if (!dependencyMap.has(targetId)) {
        dependencyMap.set(targetId, []);
      }
      dependencyMap.get(targetId)!.push(sourceId);
    }
  }

  // Find nodes with multiple dependencies
  for (const [targetId, sourceIds] of dependencyMap.entries()) {
    if (sourceIds.length > 1) {
      const targetNode = qaPairs.find(p => normalizeNodeId(p.question.id) === targetId);
      const targetLabel = targetNode?.question.breadcrumb || targetId;

      conflicts.push({
        type: 'multiple',
        affectedNodes: [targetId, ...sourceIds],
        description: `${sourceIds.length} answers depend on "${targetLabel}" (${targetId}), which may create conflicting requirements or constraints.`,
        severity: sourceIds.length > 3 ? 'high' : 'medium',
      });
    }
  }

  return conflicts;
}

/**
 * Detect circular dependencies using depth-first search
 */
function detectCircularDependencies(qaPairs: QAPair[]): Conflict[] {
  const conflicts: Conflict[] = [];

  // Build adjacency list for dependency graph
  const graph = new Map<string, string[]>();

  for (const pair of qaPairs) {
    const sourceId = normalizeNodeId(pair.question.id);
    const targets: string[] = [];

    for (const dep of pair.answer.dependencies) {
      targets.push(normalizeNodeId(dep.targetId));
    }

    graph.set(sourceId, targets);
  }

  // Track visited nodes and current recursion stack
  const visited = new Set<string>();
  const recursionStack = new Set<string>();
  const cycles: string[][] = [];

  // DFS to detect cycles
  function dfs(nodeId: string, path: string[]): void {
    visited.add(nodeId);
    recursionStack.add(nodeId);
    path.push(nodeId);

    const neighbors = graph.get(nodeId) || [];

    for (const neighbor of neighbors) {
      if (!visited.has(neighbor)) {
        dfs(neighbor, [...path]);
      } else if (recursionStack.has(neighbor)) {
        // Found a cycle
        const cycleStart = path.indexOf(neighbor);
        if (cycleStart !== -1) {
          const cycle = [...path.slice(cycleStart), neighbor];
          cycles.push(cycle);
        }
      }
    }

    recursionStack.delete(nodeId);
  }

  // Run DFS from each unvisited node
  for (const nodeId of graph.keys()) {
    if (!visited.has(nodeId)) {
      dfs(nodeId, []);
    }
  }

  // Convert cycles to conflicts
  for (const cycle of cycles) {
    // Remove duplicate cycles (same nodes in different order)
    const cycleKey = [...cycle].sort().join('->');
    const isDuplicate = conflicts.some(c => {
      const existingKey = [...c.affectedNodes].sort().join('->');
      return existingKey === cycleKey;
    });

    if (!isDuplicate) {
      const cycleDescription = cycle
        .map(id => {
          const node = qaPairs.find(p => normalizeNodeId(p.question.id) === id);
          return node?.question.breadcrumb || id;
        })
        .join(' → ');

      conflicts.push({
        type: 'circular',
        affectedNodes: cycle,
        description: `Circular dependency detected: ${cycleDescription}`,
        severity: 'high',
      });
    }
  }

  return conflicts;
}

/**
 * Normalize node IDs for comparison
 * Some dependencies might reference just "How.1" while full ID is "Root.How.1"
 */
function normalizeNodeId(id: string): string {
  // Already normalized
  if (id.startsWith('Root.')) {
    return id;
  }

  // Check if it's a short reference like "How.1"
  if (/^[A-Za-z]+\.\d+$/.test(id)) {
    // This might be a root reference
    return id; // Keep as-is for now, let matching handle it
  }

  return id;
}

/**
 * Get conflicts for a specific node ID
 */
export function getConflictsForNode(nodeId: string, allConflicts: Conflict[]): Conflict[] {
  const normalizedId = normalizeNodeId(nodeId);

  return allConflicts.filter(conflict =>
    conflict.affectedNodes.some(id => normalizeNodeId(id) === normalizedId)
  );
}

/**
 * Check if a node has any conflicts
 */
export function hasConflicts(nodeId: string, allConflicts: Conflict[]): boolean {
  return getConflictsForNode(nodeId, allConflicts).length > 0;
}
